import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, getToken, setToken } from "@/lib/api";

const AuthCtx = createContext(null);

// Lê o e-mail/expiração do próprio JWT (sem validar a assinatura — quem valida é o servidor).
function userFromToken(t) {
  try {
    const p = JSON.parse(atob(t.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    if (p.exp && p.exp * 1000 <= Date.now()) return null;
    return { email: p.email, role: "admin" };
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = verificando, false = deslogado

  useEffect(() => {
    const t = getToken();
    if (!t) return setUser(false);
    const local = userFromToken(t);
    if (!local) {
      setToken(null);
      return setUser(false);
    }
    let alive = true;
    let timer;
    // Só um 401 (token inválido/expirado) desloga. Servidor lento, rede caindo ou 5xx mantêm a sessão
    // (o token continua válido) e a verificação é repetida em segundo plano.
    const check = (attempt) =>
      api
        .get("/auth/me", { timeout: 15000 })
        .then((r) => alive && setUser(r.data))
        .catch((err) => {
          if (!alive) return;
          if (err.response?.status === 401) return setUser(false);
          setUser((u) => u || local);
          timer = setTimeout(() => alive && check(attempt + 1), Math.min(30000, 2000 * 2 ** attempt));
        });
    check(0);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    const onLogout = () => setUser(false);
    window.addEventListener("axon:logout", onLogout);
    return () => window.removeEventListener("axon:logout", onLogout);
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setToken(data.token);
    setUser(data.user);
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setUser(false);
  }, []);

  return <AuthCtx.Provider value={{ user, login, logout }}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
